// src/features/labTests/labTestsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from "../../../services/api";
import { GET_PHARMACY_DETAILS, UPDATE_PHARMACY_DETAILS } from "../../../services/urls";
import { getPharmacyId } from '../../../utils/commonFunctions';

// Nylas API URL and endpoints
const NYLAS_API_URL = 'https://api.us.nylas.com/v3';
const NYLAS_CALENDAR_URL = `${NYLAS_API_URL}/calendars/SconDvanYgjrlv2eFZ745TF6lNcFfg89Qiynj1dStfmHCKM7fnWtvF45pddPcIg54Hkjgkg01gMdLS_kHVy_8VWVb9M4jWZm`;

// Initial state for pharmacy details and Nylas connection state
const initialState = {
  loading: false,
  updateLoading: false,
  pharmacyDetails: [],
  error: null,
  accessToken: null,
  calendarDetails: null,
  calendarLoading: false,
  calendarError: null,
  connectedCalendars: [],
  fetchCalendarsLoading: false,
  fetchCalendarsError: null,
  disconnectCalendarLoading: false,
  disconnectCalendarError: null,
};

// Async thunk to fetch pharmacy details
export const fetchPharmacyDetails = createAsyncThunk(
  'pharmacy/loggedInDetails',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_PHARMACY_DETAILS + `/${getPharmacyId()}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch pharmacy details");
    }
  }
);

// Async thunk to update pharmacy details
export const updatePharmacyDetails = createAsyncThunk(
  'pharmacy/updatePharmacyDetails',
  async (pharmacyDetails, { rejectWithValue }) => {
    try {
      const response = await api.put(UPDATE_PHARMACY_DETAILS + `/${getPharmacyId()}`, pharmacyDetails);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update the pharmacy details");
    }
  }
);

// Async thunk to update pharmacy schedule details
export const updatePharmacyScheduleDetails = createAsyncThunk(
  'pharmacy/updatePharmacyScheduleDetails',
  async (pharmacyDetails, { rejectWithValue }) => {
    try {
      const response = await api.put(UPDATE_PHARMACY_DETAILS + `/${getPharmacyId()}/update-schedule-fields`, pharmacyDetails);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update the pharmacy schedule details");
    }
  }
);

// Async thunk to connect to Nylas calendar
export const connectCalendar = createAsyncThunk(
  'pharmacy/connectCalendar',
  async (_, { rejectWithValue }) => {
    try {
      // Trigger OAuth flow to get access token (implement on backend or via front-end logic)
      const accessToken = "user_access_token_from_oauth"; // Simulate OAuth flow, replace with real access token
      return accessToken;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to connect to the calendar");
    }
  }
);

// Async thunk to fetch calendar details
export const fetchCalendarDetails = createAsyncThunk(
  'pharmacy/fetchCalendarDetails',
  async (accessToken, { rejectWithValue }) => {
    try {
      const response = await fetch(NYLAS_CALENDAR_URL, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch calendar details");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch calendar details");
    }
  }
);

// Async thunk to fetch connected calendars for a pharmacy
export const fetchConnectedCalendars = createAsyncThunk(
  'pharmacy/fetchConnectedCalendars',
  async (pharmacyId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${UPDATE_PHARMACY_DETAILS}/${pharmacyId}/calendars`);
      console.log("Calendar API response:", response.data);
      
      // Based on the screenshot, the response format looks like:
      // { data: { success: true, calendars: [{ ... }] } }
      // OR
      // { success: true, calendars: [{ ... }] }
      
      if (response.data.data && response.data.data.calendars) {
        return response.data.data.calendars;
      } else if (response.data.calendars) {
        return response.data.calendars;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      // Additional check for a single object with calendar properties
      if (response.data.data && response.data.data.calendarId) {
        return [response.data.data];
      } else if (response.data.calendarId) {
        return [response.data];
      }
      
      // If none of the above, return an empty array as fallback
      console.warn('Unexpected calendar response format:', response.data);
      return [];
    } catch (error) {
      console.error('Calendar fetch error:', error);
      return rejectWithValue(error.response?.data?.message || "Failed to fetch connected calendars");
    }
  }
);

// Async thunk to disconnect a calendar
export const disconnectCalendar = createAsyncThunk(
  'pharmacy/disconnectCalendar',
  async ({ pharmacyId, calendarId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${UPDATE_PHARMACY_DETAILS}/${pharmacyId}/calendars/${calendarId}/disconnect`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to disconnect calendar");
    }
  }
);

// Add this new action for connecting calendar
export const connectNylasCalendar = createAsyncThunk(
  'pharmacyDetails/connectNylasCalendar',
  async ({ pharmacyId, authorizationCode, redirectUri }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post(`${UPDATE_PHARMACY_DETAILS}/${pharmacyId}/calendars`, {
        authorizationCode,
        redirectUri
      });
      
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'An error occurred while connecting calendar');
    }
  }
);

// Create slice with added calendar connection and fetching logic
const scheduleSettingsSlice = createSlice({
  name: 'pharmacyDetails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Handle pharmacy details fetch and update
    builder
      .addCase(fetchPharmacyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPharmacyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.pharmacyDetails = action.payload;
      })
      .addCase(fetchPharmacyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePharmacyDetails.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updatePharmacyDetails.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.error = null;
      })
      .addCase(updatePharmacyDetails.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      
      // Handle calendar connection
      .addCase(connectCalendar.pending, (state) => {
        state.calendarLoading = true;
        state.calendarError = null;
      })
      .addCase(connectCalendar.fulfilled, (state, action) => {
        state.accessToken = action.payload;
        state.calendarLoading = false;
      })
      .addCase(connectCalendar.rejected, (state, action) => {
        state.calendarLoading = false;
        state.calendarError = action.payload;
      })
      
      // Handle fetching calendar details
      .addCase(fetchCalendarDetails.pending, (state) => {
        state.calendarLoading = true;
        state.calendarError = null;
      })
      .addCase(fetchCalendarDetails.fulfilled, (state, action) => {
        state.calendarLoading = false;
        state.calendarDetails = action.payload;
      })
      .addCase(fetchCalendarDetails.rejected, (state, action) => {
        state.calendarLoading = false;
        state.calendarError = action.payload;
      })

      // Handle connecting calendar
      .addCase(connectNylasCalendar.pending, (state) => {
        state.calendarLoading = true;
        state.calendarError = null;
      })
      .addCase(connectNylasCalendar.fulfilled, (state, action) => {
        state.calendarLoading = false;
        state.calendarDetails = action.payload;
      })
      .addCase(connectNylasCalendar.rejected, (state, action) => {
        state.calendarLoading = false;
        state.calendarError = action.payload;
      })
      
      // Handle fetch connected calendars
      .addCase(fetchConnectedCalendars.pending, (state) => {
        state.fetchCalendarsLoading = true;
        state.fetchCalendarsError = null;
      })
      .addCase(fetchConnectedCalendars.fulfilled, (state, action) => {
        state.fetchCalendarsLoading = false;
        
        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.connectedCalendars = action.payload;
        } else if (typeof action.payload === 'object' && action.payload !== null) {
          // If it's a single calendar object, wrap it in an array
          state.connectedCalendars = [action.payload];
        } else {
          state.connectedCalendars = [];
        }
        
        console.log("Updated connectedCalendars state:", state.connectedCalendars);
      })
      .addCase(fetchConnectedCalendars.rejected, (state, action) => {
        state.fetchCalendarsLoading = false;
        state.fetchCalendarsError = action.payload;
      })
      
      // Handle disconnect calendar
      .addCase(disconnectCalendar.pending, (state) => {
        state.disconnectCalendarLoading = true;
        state.disconnectCalendarError = null;
      })
      .addCase(disconnectCalendar.fulfilled, (state) => {
        state.disconnectCalendarLoading = false;
        state.connectedCalendars = [];
      })
      .addCase(disconnectCalendar.rejected, (state, action) => {
        state.disconnectCalendarLoading = false;
        state.disconnectCalendarError = action.payload;
      });
  }
});

// Export the reducer
export default scheduleSettingsSlice.reducer;
